import { gql, useLazyQuery } from '@apollo/client';
import { getOrgDefaultLanguage } from '@assets/i18n/config';
import { Languages } from '@onevesthq/ov-enums';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useEffect } from 'react';
import { loadScript, loadScriptsConcurrently } from './loadScript';
import { applyLsegMainIndexEciSupport } from './lsegMainIndexEciSupport';
import {
  type LanguageTag,
  useTranslatedMessages,
} from './useTranslatedMessages';

const isEciRic = (ric: string): boolean =>
  ric.includes('=ECIX') || ric.endsWith('=ECI');

const refinitivOptionsIncludeEciRic = (widgetOptions: unknown): boolean => {
  if (!widgetOptions || typeof widgetOptions !== 'object') {
    return false;
  }
  const opts = widgetOptions as Record<string, unknown>;
  if (typeof opts.ric === 'string' && isEciRic(opts.ric)) {
    return true;
  }
  if (Array.isArray(opts.rics)) {
    return opts.rics.some((ric) => typeof ric === 'string' && isEciRic(ric));
  }
  return false;
};

const FETCH_LSEG_WIDGETS = gql`
  query fetchLsegSamlRequest($input: FetchLsegSamlRequestInput!) {
    fetchLsegSamlRequest(input: $input) {
      samlRequest
    }
  }
`;

declare global {
  // biome-ignore lint/style/useConsistentTypeDefinitions: override module
  interface Window {
    dory: any; // comes from refinitiv/sdk.v6.3.0.js
    fdc__widgets__: any; // comes from refinitiv/main.v2.6.7.js
    createShadowDOM: (
      selector: string,
      stylesheets: string[],
    ) => HTMLDivElement | undefined; // comes from refinitiv/helpers.js
  }
}
export type RefinitivWidgetType =
  | 'WMainIndex'
  | 'WMarketList'
  | 'WMarketTiles'
  | 'WTickerBand'
  | 'WNewsHeadlines'
  | 'WInterestRate'
  | 'WInstrumentInfo'
  | 'WPriceChart'
  | 'WEstimatesRatios'
  | 'WCompanyProfile'
  | 'WFundAllocation'
  | 'WFundAvgAnnualReturns'
  | 'WPriceYieldChart'
  | 'WSearchSecurities'
  | 'WFundObjective';

// Refinitiv/LSEG widget bundles are served from public/refinitiv (vendor static assets).
// biome.json excludes public/refinitiv/** via files.includes so Biome does not process vendor code.
const COMMON_SCRIPTS_PRE = [
  '/refinitiv/main.v2.6.7.js',
  '/refinitiv/sdk.v6.3.0.js',
  '/refinitiv/runtime.js',
  '/refinitiv/shared.js',
];

const SPECIFIC_SCRIPTS: Record<RefinitivWidgetType, string[]> = {
  WMainIndex: [
    '/refinitiv/vendor-date.js',
    '/refinitiv/vendor-1~4~5~8~13~20~25~30~31~32~34~40~41~42.js',
    '/refinitiv/vendor-8~13~20~25~32~34.js',
    '/refinitiv/vendor-flags.js',
    '/refinitiv/WMainIndex.v2.6.7.refinitiv-light.css',
  ],
  WMarketList: [
    '/refinitiv/vendor-1~2~4~5~20~21~23~26~28~30~31~36~39~40~41~42.js',
    '/refinitiv/vendor-1~4~5~20~26~28~29~30~31~39~40~41~42.js',
    '/refinitiv/vendor-grid.js',
    '/refinitiv/vendor-26~39.js',
    '/refinitiv/vendor-flags.js',
    '/refinitiv/WMarketList.v2.6.7.refinitiv-light.css',
  ],
  WMarketTiles: [
    '/refinitiv/vendor-flags.js',
    '/refinitiv/WMarketTiles.v2.6.7.refinitiv-light.css',
  ],
  WTickerBand: [
    '/refinitiv/vendor-1~2~4~5~20~21~23~26~28~30~31~36~39~40~41~42.js',
    '/refinitiv/vendor-WTickerBand.js',
    '/refinitiv/WTickerBand.v2.6.7.refinitiv-light.css',
  ],
  WInterestRate: [
    '/refinitiv/vendor-1~2~4~5~20~21~23~26~28~30~31~36~39~40~41~42.js',
    '/refinitiv/vendor-grid.js',
    '/refinitiv/vendor-WInterestRate.js',
    '/refinitiv/vendor-flags.js',
    '/refinitiv/WInterestRate.v2.6.7.refinitiv-light.css',
  ],
  WNewsHeadlines: [
    '/refinitiv/vendor-1~2~4~5~20~21~23~26~28~30~31~36~39~40~41~42.js',
    '/refinitiv/vendor-1~4~5~20~26~28~29~30~31~39~40~41~42.js',
    '/refinitiv/vendor-grid.js',
    '/refinitiv/vendor-1~4~5~20~28~30~31~40~41~42.js',
    '/refinitiv/vendor-select.js',
    '/refinitiv/vendor-WNewsHeadlines.js',
    '/refinitiv/WNewsHeadlines.v2.6.7.refinitiv-light.css',
  ],
  WInstrumentInfo: [
    '/refinitiv/vendor-flags.js',
    '/refinitiv/WInstrumentInfo.v2.6.7.refinitiv-light.css',
  ],
  WPriceChart: [
    '/refinitiv/vendor-date.js',
    '/refinitiv/vendor-1~2~4~5~20~21~23~26~28~30~31~36~39~40~41~42.js',
    '/refinitiv/vendor-1~4~5~8~13~20~25~30~31~32~34~40~41~42.js',
    '/refinitiv/vendor-1~4~5~20~26~28~29~30~31~39~40~41~42.js',
    '/refinitiv/vendor-1~4~5~20~28~30~31~40~41~42.js',
    '/refinitiv/vendor-select.js',
    '/refinitiv/vendor-1~21~31~41.js',
    '/refinitiv/WPriceChart.v2.6.7.refinitiv-light.css',
  ],
  WEstimatesRatios: [
    '/refinitiv/vendor-date.js',
    '/refinitiv/vendor-1~4~5~8~13~20~25~30~31~32~34~40~41~42.js',
    '/refinitiv/vendor-8~13~20~25~32~34.js',
    '/refinitiv/WEstimatesRatios.v2.6.7.refinitiv-light.css',
  ],
  WCompanyProfile: [
    '/refinitiv/vendor-date.js',
    '/refinitiv/WCompanyProfile.v2.6.7.refinitiv-light.css',
  ],
  WFundAllocation: ['/refinitiv/WFundAllocation.v2.6.7.refinitiv-light.css'],
  WFundAvgAnnualReturns: [
    '/refinitiv/WFundAvgAnnualReturns.v2.6.7.refinitiv-light.css',
  ],
  WPriceYieldChart: [
    '/refinitiv/vendor-date.js',
    '/refinitiv/vendor-1~4~5~8~13~20~25~30~31~32~34~40~41~42.js',
    '/refinitiv/vendor-8~13~20~25~32~34.js',
    '/refinitiv/WPriceYieldChart.v2.6.7.refinitiv-light.css',
  ],
  WFundObjective: [
    '/refinitiv/vendor-date.js',
    '/refinitiv/vendor-1~4~5~8~13~20~25~30~31~32~34~40~41~42.js',
    '/refinitiv/vendor-8~13~20~25~32~34.js',
    '/refinitiv/WFundObjective.v2.6.7.refinitiv-light.css',
  ],
  WSearchSecurities: [],
};

const COMMON_SCRIPTS_POST = [
  '/refinitiv/vendor.v2.6.7.refinitiv-light.css',
  '/refinitiv/refinitiv-light.v2.6.7.css',
  '/refinitiv/helpers.js',
];

export const useRefinitivWidget = (
  selector: string,
  type: RefinitivWidgetType,
  options: any,
  stylesheets?: string[],
): void => {
  const [fetchSamlRequest] = useLazyQuery(FETCH_LSEG_WIDGETS);
  const { activeOrganization, userContext } = useContext(UserContext);
  const language =
    userContext.language ?? getOrgDefaultLanguage({ asEnum: true });

  useEffectOnce(async () => {
    await loadScriptsConcurrently(COMMON_SCRIPTS_PRE);
    await loadScript('/refinitiv/sdk-workaround.js');
    await loadScriptsConcurrently(SPECIFIC_SCRIPTS[type]);
    await loadScript(`/refinitiv/${type}.js`);
    await loadScriptsConcurrently(COMMON_SCRIPTS_POST);

    if (!window.dory) {
      return;
    }

    if (refinitivOptionsIncludeEciRic(options)) {
      applyLsegMainIndexEciSupport(window.dory);
    }

    /* authentication */
    window.dory.config.setBasePath('https://lseg-widgets.financial.com');
    window.dory.Auth.isSessionValid().then(async (isSessionValid: boolean) => {
      if (isSessionValid === true) {
        initWidget();
      } else {
        const response = await fetchSamlRequest({
          variables: { input: { organizationId: activeOrganization.id } },
        });
        window.dory.Auth.samlLogin(
          response.data.fetchLsegSamlRequest.samlRequest,
        )
          .then(() => {
            initWidget();
          })
          .catch((error: any) => {
            console.log(error);
          });
      }
    });
  });

  /*
   * Locale mapping for Refinitiv widgets.
   *
   * Limitation: App doesn't track individual user's country, only org-level
   * allowed countries (applicableLocalization.countries). We use sensible defaults:
   * - French → 'fr-CA' (Canadian French - primary market)
   * - Spanish → 'es-US' (US Spanish)
   *
   * Future: If user country becomes available (e.g., from userContext),
   * construct locale dynamically: `${langCode}-${userCountry}`
   */
  let locale: LanguageTag | undefined;
  switch (language) {
    case Languages.FRENCH:
      locale = 'fr-CA';
      break;
    case Languages.SPANISH:
      locale = 'es-US';
      break;
    case Languages.ENGLISH:
    default:
      locale = undefined; // Refinitiv uses English as default
  }

  const localeMessages = useTranslatedMessages(type, locale);

  /* boot the widget */
  const initWidget = () => {
    let widget;
    try {
      if (stylesheets?.length) {
        const widgetCssFiles = [
          ...COMMON_SCRIPTS_PRE,
          ...SPECIFIC_SCRIPTS[type],
          ...COMMON_SCRIPTS_POST,
        ].filter((s) => s.endsWith('.css'));
        const allStylesheets = [...widgetCssFiles, ...stylesheets];
        const container = window.createShadowDOM(selector, allStylesheets);
        if (!container) return;
        widget = window.fdc__widgets__[type].init(container, options);
      } else {
        widget = window.fdc__widgets__[type].init(selector, options);
      }
      if (options.onClick) widget.onClick((e: any) => options.onClick(e));
    } catch (error) {
      console.error(`initWidget: Error initializing widget ${type}:`, error);
    }

    if (locale) {
      try {
        widget.setLocale(locale);
        widget.setLocaleMessages(locale, localeMessages);
      } catch (error) {
        console.error(
          `initWidget: Error setting ${type} widget locale (${locale}):`,
          error,
        );
      }
    }
  };
};

function useEffectOnce(effectFn: () => void) {
  useEffect(() => {
    effectFn();
  }, []);
}
